import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import TableSchemaBuilder from "../../../Questions Module/TableSchemaBuilder";
import { detailsToSchema, schemaToDetails } from "../../../Questions Module/TableSchemaBuilder/schemaDetails";
import { hasTableSetup } from "../../model/helpers";
import { tableSummary } from "../../model/tableSetup";

/**
 * The rows and columns a tabular KPI is answered in. The cell reads
 * the table back in one line; editing opens the question form's own
 * TableSchemaBuilder, so a table built here is stored exactly as one built
 * there.
 *
 * @param {object} props
 * @param {string} props.type question type
 * @param {object[]} props.details stored rows
 * @param {string} props.title KPI title, for the dialog heading
 * @param {boolean} [props.dirty]
 * @param {string} [props.error]
 * @param {(details: object[]) => void} props.onChange
 */
function TableSetupCell({ type, details, title, dirty, error, onChange }) {
  const [open, setOpen] = useState(false);
  const summary = useMemo(() => tableSummary(type, details), [type, details]);

  if (!hasTableSetup(type)) return <span className="kgb-readonly kgb-muted">Not applicable</span>;

  const classes = ["kgb-chipcell", dirty ? "kgb-chipcell--dirty" : "", error ? "kgb-chipcell--invalid" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} title={error || undefined}>
      {summary ? <span className="kgb-chip kgb-chip--plain">{summary}</span> : null}
      <button type="button" className="kgb-add-link" onClick={() => setOpen(true)}>
        {summary ? "Edit" : "+ Set up table"}
      </button>
      {open ? (
        <TableSetupDialog
          type={type}
          details={details}
          title={title}
          onApply={(next) => {
            onChange(next);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * TableSchemaBuilder reports its schema on mount, again once the stored table
 * has loaded into it, and on every edit. Reports before the first click or
 * keystroke are the table as stored, so Apply only writes when a later one
 * differs.
 */
function TableSetupDialog({ type, details, title, onApply, onClose }) {
  const initialSchema = useMemo(() => detailsToSchema(type, details), [type, details]);
  const baseline = useRef(null);
  const latest = useRef(null);
  const touched = useRef(false);

  const onSchema = useCallback((schema) => {
    if (!touched.current) baseline.current = JSON.stringify(schema);
    latest.current = schema;
  }, []);
  const touch = () => {
    touched.current = true;
  };

  const apply = () => {
    const next = latest.current && JSON.stringify(latest.current) !== baseline.current
      ? schemaToDetails(latest.current)
      : null;
    if (next) onApply(next);
    else onClose();
  };

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable className="kgb-modal">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{title ? `Table setup — ${title}` : "Table setup"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div onClickCapture={touch} onChangeCapture={touch}>
          <TableSchemaBuilder tableSchemaForEdit={initialSchema} setTableSchema={onSchema} />
        </div>
      </Modal.Body>
      <Modal.Footer className="kgb-root">
        <button type="button" className="kgb-btn" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="kgb-btn kgb-btn--primary" onClick={apply}>
          Apply
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default memo(TableSetupCell);
